import { useEffect, useState } from "react";
import { Truck, PlusCircle, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const EMPTY = { name: "", contact: "", email: "", phone: "", category: "", password: "" };

export const SuppliersSection = ({ onChanged }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorConfirm, setVendorConfirm] = useState(null);

  const loadVendors = () => api.get("/procurement/direct-vendors").then((r) => setVendors(r.data.vendors || [])).catch(() => {});
  useEffect(() => { loadVendors(); }, []);

  const deleteVendor = async (name) => {
    try {
      await api.post("/procurement/direct-vendors/delete", { name });
      toast.success(`"${name}" hafızadan silindi`);
      setVendorConfirm(null);
      loadVendors();
    } catch (e) {
      toast.error(formatApiError(e, "Silinemedi"));
    }
  };

  const load = () => api.get("/suppliers").then((r) => { setSuppliers(r.data); onChanged?.(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success("Tedarikçi güncellendi");
      } else {
        const r = await api.post("/suppliers", form);
        if (r.data.email_sent) {
          toast.success("Tedarikçi eklendi · Giriş bilgileri e-postayla gönderildi");
        } else if (r.data.account_created) {
          toast.warning(`Hesap oluşturuldu ama e-posta gönderilemedi. Şifre: ${r.data.generated_password || "girdiğiniz şifre"}`, { duration: 15000 });
        } else if (r.data.account_exists) {
          toast.info("Tedarikçi eklendi · Bu e-posta zaten bir kullanıcı hesabına bağlı");
        } else {
          toast.success("Tedarikçi eklendi (e-posta girilmediği için giriş hesabı oluşturulmadı)");
        }
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`${s.name} tedarikçisi silinsin mi?`)) return;
    try {
      await api.delete(`/suppliers/${s.id}`);
      toast.success("Tedarikçi silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="border border-stone-200 bg-white" data-testid="suppliers-section">
      <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
          <Truck size={14} className="text-[#1C4E35]" /> Tedarikçiler ({suppliers.length})
        </p>
        <Button variant="outline" size="sm" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-supplier-button" className="h-8 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95">
          <PlusCircle size={13} className="mr-1.5" /> Tedarikçi Ekle
        </Button>
      </div>
      {suppliers.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-stone-400" data-testid="suppliers-empty">Henüz tedarikçi eklenmedi. Tedarik işaretlemesi için önce tedarikçi ekleyin.</p>
      ) : (
        <Table data-testid="suppliers-table">
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Firma</TableHead>
              <TableHead className="text-xs">Yetkili</TableHead>
              <TableHead className="text-xs">İletişim</TableHead>
              <TableHead className="text-xs">Kategori</TableHead>
              <TableHead className="text-right text-xs">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map((s) => (
              <TableRow key={s.id} className="hover:bg-stone-50" data-testid={`supplier-row-${s.name}`}>
                <TableCell className="py-2 text-sm font-medium">{s.name}</TableCell>
                <TableCell className="py-2 text-sm">{s.contact || "—"}</TableCell>
                <TableCell className="py-2 text-xs text-stone-500">
                  {s.email || "—"}
                  {s.phone && <p>{s.phone}</p>}
                </TableCell>
                <TableCell className="py-2 text-sm">{s.category || "—"}</TableCell>
                <TableCell className="py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setForm({ name: s.name, contact: s.contact || "", email: s.email || "", phone: s.phone || "", category: s.category || "" }); setOpen(true); }} data-testid={`edit-supplier-${s.name}`} className="h-8 rounded-sm px-2 hover:bg-stone-100">
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`delete-supplier-${s.name}`} className="h-8 rounded-sm px-2 text-red-600 hover:bg-red-50">
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className="border-t border-stone-200 px-4 py-3" data-testid="direct-vendors-section">
        <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Serbest Satıcılar (Doğrudan Satınalma Hafızası)</p>
        {vendors.length === 0 ? (
          <p className="mt-1.5 text-xs text-stone-400" data-testid="direct-vendors-empty">Henüz serbest satıcı kaydı yok.</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {vendors.map((v) => (
              <span key={v} className="flex items-center gap-1.5 border border-stone-300 bg-stone-50 px-2 py-1 text-xs text-stone-700" data-testid={`direct-vendor-${v.replace(/\s+/g, "_")}`}>
                {v}
                {vendorConfirm === v ? (
                  <>
                    <button type="button" onClick={() => deleteVendor(v)} data-testid={`direct-vendor-confirm-${v.replace(/\s+/g, "_")}`} className="font-medium text-red-600 hover:underline">Sil</button>
                    <button type="button" onClick={() => setVendorConfirm(null)} className="text-stone-500 hover:underline">Vazgeç</button>
                  </>
                ) : (
                  <button type="button" onClick={() => setVendorConfirm(v)} title="Satıcıyı hafızadan sil" data-testid={`direct-vendor-delete-${v.replace(/\s+/g, "_")}`} className="text-stone-400 transition-colors hover:text-red-600">
                    <Trash2 size={12} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-md" data-testid="supplier-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight">{editing ? "Tedarikçiyi Düzenle" : "Yeni Tedarikçi"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {[["name", "Firma Adı *"], ["contact", "Yetkili Kişi"], ["email", "E-posta"], ["phone", "Telefon"], ["category", "Kategori (ör. Mobilya, AV)"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">{label}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`supplier-form-${k}`} className="rounded-sm border-stone-300" />
              </div>
            ))}
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">Şifre</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Boş bırakılırsa otomatik oluşturulur" data-testid="supplier-form-password" className="rounded-sm border-stone-300" />
                <p className="text-[11px] text-stone-400">E-posta girildiyse tedarikçiye giriş hesabı açılır ve bilgileri (şifre dahil) e-postayla gönderilir.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button onClick={save} disabled={saving || !form.name.trim()} data-testid="supplier-form-submit" className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
